import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation } from '@tanstack/react-query';
import { transferDocuments } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { useState } from 'react';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface DriveTransferDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveTransferDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  isAllUsers,
}: DriveTransferDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [searchedUser, setSearchedUser] = useState('');
  const [textConfirmation, setTextConfirmation] = useState('');
  const [sendSummaryEmail, setSendSummaryEmail] = useState(false);
  const [searchedGroupOrUser, setSearchedGroupOrUser] = useState('');
  const [userList, setUserList] = useState<string[]>([]);
  const { setSnackbar } = useStore();
  const { adminEmail } = useUserSettings();

  function clearForm() {
    setCurrentIndex(0);
    setSearchedUser('');
    setTextConfirmation('');
    setSendSummaryEmail(false);
    setSearchedGroupOrUser('');
    setUserList([]);
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      transferDocuments(
        {
          srcUser: userEmail,
          destUser: searchedUser,
          notifyByEmail: sendSummaryEmail,
        },
        userList,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to transfer all folders and documents has started.',
      });

      clearForm();
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue trying to start the transfer. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.transfer_drive, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.transfer_drive,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (currentIndex !== 2) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      if (
        isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanTransferDocs.value)
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        clearForm();
        handleClose();
      }}
      title="Transfer Ownership"
      actingAs={adminEmail}
      dialogContent={
        <Box height={250}>
          {currentIndex === 0 ? (
            <Stack>
              <Typography
                sx={{
                  my: 1,
                }}
              >
                Transfer <strong>{userEmail}'s</strong> entire drive to:
              </Typography>
              <Box sx={{ my: 1 }}>
                <SharedAutocomplete
                  sources={['users']}
                  autocompleteValue={searchedUser}
                  setAutoCompleteValue={setSearchedUser}
                />
              </Box>
            </Stack>
          ) : null}

          {currentIndex === 1 ? (
            <Stack gap={2}>
              <Alert severity="warning">
                You are about to transfer the{' '}
                <span style={{ fontWeight: 'bold', wordBreak: 'break-all' }}>
                  entire drive of {userEmail} to {searchedUser}
                </span>
                . This action is irreversible. Please confirm that you want to
                transfer the entire drive by typing "
                <span style={{ fontWeight: 'bold' }}>
                  transfer entire drive
                </span>
                " in the text field below.
              </Alert>
              <TextField
                fullWidth
                label="Text Confirmation"
                placeholder="transfer entire drive"
                color="warning"
                value={textConfirmation}
                onChange={(e) => setTextConfirmation(e.target.value)}
              />
            </Stack>
          ) : null}
          {currentIndex === 2 ? (
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendSummaryEmail}
                    onChange={(e) => setSendSummaryEmail(e.target.checked)}
                  />
                }
                label="Send summary email"
              />
              <Stack alignItems="center">
                <Stack flexDirection="row" gap={1} sx={{ my: 1 }}>
                  <Box width={400}>
                    <SharedAutocomplete
                      sources={['users', 'groups']}
                      autocompleteValue={searchedGroupOrUser}
                      setAutoCompleteValue={setSearchedGroupOrUser}
                      disabled={!sendSummaryEmail}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    disabled={!sendSummaryEmail}
                    onClick={() => {
                      if (!userList.includes(searchedGroupOrUser)) {
                        setUserList([...userList, searchedGroupOrUser]);
                      }
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                <Paper sx={{ width: 470, height: 143, mt: 1 }}>
                  <TableContainer sx={{ height: 143, overflowY: 'auto' }}>
                    <Table size={'small'} stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Emails to Notify</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userList.map((user) => {
                          return (
                            <TableRow key={user}>
                              <TableCell>
                                <Stack
                                  flexDirection="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography variant="body2">
                                    {user}
                                  </Typography>
                                  <Tooltip arrow title="Delete">
                                    <span>
                                      <IconButton
                                        onClick={() => {
                                          setUserList((prev) =>
                                            prev.filter(
                                              (email) => email !== user
                                            )
                                          );
                                        }}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Stack>
            </Stack>
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: currentIndex === 2 ? 'Transfer Ownership' : 'Next',
        action: handleSubmit,
        loading: isPending,
        disabled:
          searchedUser === '' ||
          (currentIndex === 1 &&
            textConfirmation.trim().toLowerCase() != 'transfer entire drive') ||
          isPending,
        color: currentIndex == 2 ? 'warning' : 'primary',
      }}
      secondaryButton={
        currentIndex !== 0
          ? {
              title: 'Back',
              action: () => {
                setCurrentIndex((prev) => prev - 1);

                if (currentIndex === 1) {
                  setSearchedUser('');
                }
              },
              loading: false,
              disabled: isPending,
            }
          : undefined
      }
    />
  );
}
